import { Component } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import type { ReactReduxContextValue } from 'react-redux'
import { connect, ReactReduxContext } from 'react-redux'

import InteractivePropertyMap from 'components/Common/PropertyMap/InteractivePropertyMap'
import InfoWindow from './InfoWindow'
import { getWidth } from 'lib/window/getViewport'
import { breakpointSizes } from 'components/utils/breakpoint'
import { getMarkersByOffers, formatMarkers, overlapMarkers } from 'components/Common/PropertyMap/utils'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import {
  productClickViewDetails,
  mapPropertyPinClick,
  mapOfferInfoClick,
  mapPinOfferView,
} from 'actions/AnalyticsActions'
import noop from 'lib/function/noop'
import type { PropertyMapHeight, PropertyMapOptions } from './types'

const MapWrapper = styled.div`
  overflow: hidden;
  position: relative;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  inset-inline: 0;
  background: rgba(255, 255, 255, .3);
  display: flex;
  align-items: center;
  justify-content: center;
`

export interface DisplayState {
  height: number
  mobileInterface: boolean
}

interface Props {
  currencyCode: string
  regionCode: string
  offers?: Array<App.Offer | App.OfferSummary>
  markers?: Array<App.MapMarker>
  markerOptions?: {
    xOffset: number
    yOffset: number
  }
  loaded: boolean
  mobileInterface?: boolean
  regionHasFlights?: boolean
  height: PropertyMapHeight
  mapOptions?: PropertyMapOptions
  mapOfferInfoClick: (data: App.Offer | App.OfferSummary) => void
  productClickViewDetails: (data: App.Offer | App.OfferSummary) => void
  onSelectPropertyMarker?: (item: App.MapMarker) => void
  onUpdateDisplay?: (stateUpdate: DisplayState) => void
  onPropertyPinClick: (data: App.MapPropertyItem) => void
  onPinOfferView: (data: App.MapPropertyItem) => void
  button?: React.ReactNode
  wrapperStyle?: {
    [key: string]: any
  }
}

interface State {
  markers: Array<App.MapMarker>
}

class MultipleMarkers extends Component<React.PropsWithChildren<Props>, State> {
  mapComponent: any = null

  elements = {
    map: React.createRef<any>(),
  }

  state: State = {
    markers: [],
  }

  static defaultProps = {
    markerOptions: {
      xOffset: 0,
      yOffset: 0,
    },
    onSelectPropertyMarker: noop,
  }

  setMarkers = (offers: Array<App.Offer | App.OfferSummary>) => {
    this.setState({
      markers: getMarkersByOffers(offers, this.props.regionCode),
    })
  }

  componentDidMount() {
    const offers = this.props.offers ?? []
    if (offers.length) {
      this.setMarkers(offers)
    }
    this.updateDisplay()
    window.addEventListener('resize', this.updateDisplay)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDisplay)
  }

  componentDidUpdate(prevProps: Props) {
    const prevOffers = prevProps.offers ?? []
    const offers = this.props.offers ?? []
    if (prevOffers.length !== offers.length || this.state.markers.length !== offers.length) {
      this.setMarkers(offers)
    }
  }

  getOfferByMarker = (marker: App.MapMarker) => {
    if (marker.type === PROPERTY_MAP_MARKER_TYPE && marker.propertyItem) {
      return this.props.offers?.find(offer => offer.id === marker.propertyItem?.offerId)
    }
  }

  onProductClick = (marker: App.MapMarker) => {
    const offer = this.getOfferByMarker(marker)
    if (offer) {
      this.props.mapOfferInfoClick(offer)
      this.props.productClickViewDetails(offer)
    }
  }

  visibleHeight = () => {
    if (typeof document !== 'undefined' && document.getElementById('visible-height-reference')) {
      return document.getElementById('visible-height-reference')?.clientHeight ?? 500
    }
    return 500 // For SSR - use arbitrary value
  }

  updateDisplay = () => {
    this.props.onUpdateDisplay?.({
      height: this.visibleHeight(),
      mobileInterface: getWidth() < breakpointSizes.tablet.min,
    })
  }

  focusProperty = (offerId: string) => {
    const marker = this.state.markers.filter(x => x.type === PROPERTY_MAP_MARKER_TYPE).find(x => x.propertyItem?.offerId === offerId)
    if (marker) {
      this.elements.map.current.focusMarker(marker.id)
    }
  }

  onSelectMarker = (marker: App.MapMarker) => {
    if (marker.type === PROPERTY_MAP_MARKER_TYPE && marker.propertyItem) {
      this.props.onPinOfferView(marker.propertyItem)
      this.props.onPropertyPinClick(marker.propertyItem)
      this.props.onSelectPropertyMarker?.(marker)
    }
  }

  onDrag = () => {
    this.updateDisplay()
  }

  renderInfoWindow = (marker: App.MapMarker, onCloseClick: () => void): React.ReactNode => {
    const { currencyCode, regionCode, regionHasFlights } = this.props

    return <ReactReduxContext.Consumer>
      {({ store }: ReactReduxContextValue<App.State>) =>
        <InfoWindow
          marker={marker}
          offer={this.getOfferByMarker(marker)}
          currencyCode={currencyCode}
          regionCode={regionCode}
          showFlights={regionHasFlights}
          onCloseClick={onCloseClick}
          onProductClick={this.onProductClick}
          store={store}
        />}
    </ReactReduxContext.Consumer>
  }

  render() {
    const {
      loaded,
      button: Button,
      mobileInterface,
      mapOptions,
      wrapperStyle,
      height,
      markerOptions,
    } = this.props

    const propertyMapOptions: PropertyMapOptions = {
      gestureHandling: 'greedy',
      mapTypeControl: false,
      fullscreenControl: false,
      zoomControlOptions: {
        position: 3,
      },
      minZoom: 3,
      maxZoom: 19,
      ...mapOptions,
    }

    const markers = overlapMarkers([...this.state.markers, ...formatMarkers(this.props.markers ?? [])])

    return (
      <MapWrapper>
        {loaded && (
          <>
            <InteractivePropertyMap
              className="map"
              ref={this.elements.map}
              height={height}
              markerOptions={markerOptions}
              wrapperStyle={wrapperStyle}
              markers={markers}
              onSelectMarker={this.onSelectMarker}
              onDrag={this.onDrag}
              multipleMarkers
              renderInfoWindow={mobileInterface ? undefined : this.renderInfoWindow}
              mapOptions={propertyMapOptions}
            />
            {Button}
          </>
        )}

        {!loaded && <LoadingOverlay>
          <LoadingIndicator inline />
        </LoadingOverlay>}
      </MapWrapper>
    )
  }
}

function mapStateToProps(state: App.State) {
  return ({
    regionCode: state.geo.currentRegionCode,
    currencyCode: state.geo.currentCurrency,
    regionHasFlights: state.geo.currentRegionHasFlights,
  })
}

const mapDispatchToProps = {
  productClickViewDetails,
  onPropertyPinClick: mapPropertyPinClick,
  mapOfferInfoClick,
  onPinOfferView: mapPinOfferView,
}

export default connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true })(MultipleMarkers)
